import { BRIEF_KEYS } from './creationBrief';

// 去重只处理同一次输入中重复的补充；跨轮的相同回答可能是在纠正要求。
export function dedupeSupplement(text) {
  const parts = String(text || '').split(/\n\s*\n(?:补充[：:]\s*)?|\n补充[：:]\s*/).map(value => value.trim()).filter(Boolean);
  return [...new Set(parts)].join('\n\n补充：');
}

export const conversationReferences = messages => [...new Map(messages.flatMap(item => item.references || []).map(ref => [ref.id, ref])).values()];

export function appendIntakeInput(messages, text, attachments = []) {
  const unanswered = messages.at(-1)?.role === 'user';
  const references = conversationReferences([...(unanswered ? [messages.at(-1)] : []), { references: attachments }]);
  return [...(unanswered ? messages.slice(0, -1) : messages), { role: 'user',
    content: dedupeSupplement(unanswered ? `${messages.at(-1).content}\n\n补充：${text}` : text), ...(references.length ? { references } : {}) }];
}

const excerpt = (text, max) => text.length <= max ? text : `${text.slice(0, Math.floor(max / 3))}\n[中段已收起]\n${text.slice(-(Math.floor(max * 2 / 3) - 14))}`;
export const intakeRound = messages => Math.min(5, Math.max(1, messages.filter(item => item.role === 'user').length));

export function packIntakeConversation(messages, force = false) {
  const clean = messages.map(item => ({ role: item.role, content: item.role === 'user' ? dedupeSupplement(item.content) : item.content,
    ...(item.role === 'user' && item.references?.length ? { references: item.references } : {}) }));
  if (!force && clean.length <= 7 && clean.every(item => item.content.length <= 3800) && clean.reduce((sum, item) => sum + item.content.length, 0) <= 12000) {
    return { messages: clean, compacted: clean.some((item, i) => item.content !== messages[i].content) };
  }
  if (clean.length <= 1) return { messages: clean.map(item => ({ ...item, content: excerpt(item.content, 3800) })), compacted: clean[0]?.content !== messages[0]?.content || (clean[0]?.content.length || 0) > 3800 };
  // 原始会话仍在当前页面。模型只需要早期用户原话摘要和最近两轮，避免旧回复挤占上下文。
  const tail = clean.slice(-4);
  const history = clean.slice(0, -4).filter(item => item.role === 'user').map(item => item.content);
  if (!history.length) return { messages: clean.map(item => ({ ...item, content: excerpt(item.content, item.role === 'user' ? 3800 : 500) })), compacted: true };
  const memory = dedupeSupplement(history.join('\n\n'));
  const references = conversationReferences(clean.slice(0, -4));
  return { messages: [{ role: 'user', content: `此前想法摘要（保留原话，后续修改优先）：\n${excerpt(memory, 3700)}`, ...(references.length ? { references } : {}) },
    ...tail.map(item => ({ ...item, content: excerpt(item.content, item.role === 'user' ? 3800 : 500) }))], compacted: true };
}

const LABELS = { subject: '核心想法', audience: '目标受众', duration: '时长', frame: '画面比例', style: '风格', content: '核心内容', assets: '已有素材', sound: '声音与字幕', constraints: '内容边界', setting: '故事背景', characters: '人物关系', conflict: '核心冲突', episodes: '剧本集数' };
const SHARED = ['subject', 'audience', 'style', 'constraints'];
export function selectScreeningMode(decision, mode) {
  if (!Array.isArray(BRIEF_KEYS[mode])) return decision;
  const changed = decision.mode !== mode;
  const fields = BRIEF_KEYS[mode].map(key => {
    const old = decision.brief?.fields?.find(item => item.key === key);
    return old && (!changed || SHARED.includes(key)) ? old : { key, label: LABELS[key], value: '', status: 'pending', evidence: '' };
  });
  return { ...decision, mode, content_type: mode === 'canvas' ? (!changed && decision.content_type) || 'SHORT_VIDEO' : null,
    brief: { ...decision.brief, version: 3, mode, fields, missing: fields.filter(item => item.status === 'pending').map(item => item.key),
      outline: mode === 'canvas' ? ['沿用核心想法，细化短片脚本与分镜', '核对素材和制作参数，再开始生成'] : ['沿用核心想法，展开人物关系与故事大纲', '确认篇幅和叙事方向，再开始分集写作'] } };
}

// 兼容滚动发布中的旧接口；第三轮后的收敛和网络失败恢复不能依赖旧服务放行。
export function summarizeIntake(messages, previous, { round = intakeRound(messages), fallback = false } = {}) {
  const parts = messages.filter(item => item.role === 'user').flatMap(item => dedupeSupplement(item.content).split(/\n\n(?:补充[：:]\s*)?/));
  // 摘要中同一句只留最新一次，既压掉重复补充，也保留“30秒→60秒→30秒”的最终顺序。
  const source = parts.filter((part, index) => part && parts.lastIndexOf(part) === index).join('\n');
  const summary = excerpt(source, 1000);
  const mode = ['canvas', 'writing'].includes(previous?.mode) && !fallback ? previous.mode : null;
  const base = { reply: fallback ? 'AI 暂时未能完成整理，已保留你的核心原话。请确认想法并选择创作方式，细节可以进入后继续完善。' : '核心想法已整理好。请确认下面的方向，再选择进入画布或写作台；细节可以继续完善。',
    mode: null, ready: true, title: previous?.title || messages.find(item => item.role === 'user')?.content.slice(0, 20) || '新的创作',
    summary, content_type: previous?.content_type || null, suggestions: [], screening: { round, maxRounds: 5, fallback },
    brief: { version: 3, mode: null, fields: [], missing: [], polished: `按你的原话整理：\n${summary}`, outline: ['确认核心想法与创作方式', '把已知要求带入创作，继续完善细节'] } };
  if (!mode) return base;
  // 缓存摘要可能落后于本轮修改，确认稿以本轮用户原话为准，字段只保留能在原文中找到的证据。
  const fields = (previous?.brief?.fields || []).filter(item => item.status === 'provided' && item.evidence && source.includes(item.evidence));
  return selectScreeningMode({ ...base, mode, brief: { ...base.brief, fields } }, mode);
}
